import { useContext, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Container,
  Paper,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AccountCircle, Lock, Notifications } from "@mui/icons-material";
import PersonalInfoForm from "./PersonalInfoForm";
import ChangePasswordForm from "./ChangePasswordForm";
import NotificationPreferencesForm from "./NotificationPreferencesForm";
import { useGetNotificationPreferences } from "../../hooks/query/useTenantSettings";
import { AuthContext } from "../../context/authContext";
import { useGetMyProfile } from "../../hooks/query/useUser";
import FallbackLoader from "../../components/loading/FallBackLoader";

// Custom Theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#bb0000",
      light: "#e63434",
      dark: "#880000",
    },
    background: {
      default: "#f4f4f4",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontSize: "1.5rem",
      "@media (max-width:600px)": {
        fontSize: "1.2rem",
      },
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "@media (max-width:600px)": {
            marginBottom: "16px",
          },
        },
      },
    },
  },
});

function SettingsPage(): JSX.Element {
  const { currentUser } = useContext(AuthContext);

  const [value, setValue] = useState<number>(0);

  // Media Query Hooks
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm")
  );

  const { data: personalInfo, isLoading: isPersonalLoading } =
    useGetMyProfile();

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ): void => {
    console.log(event);
    setValue(newValue);
  };

  const renderTabContent = (): JSX.Element | null => {
    switch (value) {
      case 0:
        return <PersonalInfoForm personalInfoData={personalInfo} />;
      case 1:
        return <ChangePasswordForm />;
      case 2:
        return (
          <NotificationPreferencesForm
            notificationPreferences={notificationPreferences}
          />
        );
      default:
        return null;
    }
  };

  const { data: notificationPreferences, isLoading } =
    useGetNotificationPreferences(currentUser.tenant.id);
  console.log(isLoading);

  if (isLoading || isPersonalLoading) {
    return <FallbackLoader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          minHeight: "100vh",
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2, md: 4 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 20px rgba(187, 0, 0, 0.1)",
          }}
        >
          {/* Responsive Tabs */}
          <Box
            sx={{
              width: { xs: "100%", sm: 200 },
              borderRight: { xs: "none", sm: "1px solid rgba(0,0,0,0.12)" },
              borderBottom: { xs: "1px solid rgba(0,0,0,0.12)", sm: "none" },
            }}
          >
            <Tabs
              orientation={isSmallScreen ? "horizontal" : "vertical"}
              variant="scrollable"
              value={value}
              onChange={handleTabChange}
              sx={{
                width: "100%",
                bgcolor: "white",
              }}
            >
              <Tab
                icon={<AccountCircle />}
                label={!isSmallScreen ? "Personal Info" : undefined}
                sx={{
                  flexDirection: isSmallScreen ? "row" : "column",
                  justifyContent: "center",
                  minWidth: { xs: "auto", sm: 200 },
                  px: { xs: 1, sm: 3 },
                }}
              />
              <Tab
                icon={<Lock />}
                label={!isSmallScreen ? "Password" : undefined}
                sx={{
                  flexDirection: isSmallScreen ? "row" : "column",
                  justifyContent: "center",
                  minWidth: { xs: "auto", sm: 200 },
                  px: { xs: 1, sm: 3 },
                }}
              />
              <Tab
                icon={<Notifications />}
                label={!isSmallScreen ? "Notifications" : undefined}
                sx={{
                  flexDirection: isSmallScreen ? "row" : "column",
                  justifyContent: "center",
                  minWidth: { xs: "auto", sm: 200 },
                  px: { xs: 1, sm: 3 },
                }}
              />
            </Tabs>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              p: { xs: 2, sm: 4 },
              bgcolor: "white",
              overflowY: "auto",
            }}
          >
            {renderTabContent()}
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}

export default SettingsPage;
